import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Eye, EyeOff } from 'lucide-react';
import HowItWorksHover from './HowItWorksHover';
import TermsDialog from './TermsDialog';
import AntiScamWarning from './AntiScamWarning';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginClientDialog = ({ isOpen, onOpenChange, onLogin, onRegisterSuccess }) => {
  const [activeTab, setActiveTab] = useState('login'); // 'login' or 'register'
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  const [registerData, setRegisterData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: ''
  });

  const resetForms = () => {
    setLoginData({ email: '', password: '' });
    setRegisterData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      password: '',
      confirm_password: ''
    });
    setErrorMessage('');
    setSuccessMessage('');
    setTermsAccepted(false);
    setPrivacyAccepted(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetMessage('');
    try {
      await axios.post(`${API}/auth/forgot-password?email=${encodeURIComponent(resetEmail)}`);
      setResetMessage("✅ Se l'email è registrata, riceverai le istruzioni per reimpostare la password.");
      setResetEmail('');
    } catch (_e) {
      setResetMessage("❌ Errore nell'invio. Riprova più tardi.");
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setLoading(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/auth/login-client`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(loginData)
      });

      if (!response.ok) {
        setErrorMessage('Errore: account non trovato');
        setLoading(false);
        return;
      }

      const data = await response.json();
      onOpenChange(false);
      onLogin(data);
      resetForms();
      
    } catch (error) {
      console.error('Errore:', error);
      setErrorMessage('Errore: account non trovato');
    }
    setLoading(false);
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    setLoading(true);

    // Validate passwords match
    if (registerData.password !== registerData.confirm_password) {
      setErrorMessage('Le password non coincidono');
      setLoading(false);
      return;
    }

    // Validate password: 8 chars, uppercase, lowercase, number, special char
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{8,}$/;
    if (!passwordRegex.test(registerData.password)) {
      setErrorMessage('La password deve contenere almeno 8 caratteri, una maiuscola, una minuscola, un numero e un carattere speciale');
      setLoading(false);
      return;
    }

    if (!termsAccepted) {
      setErrorMessage('Devi accettare i Termini e Condizioni per completare la registrazione.');
      setLoading(false);
      return;
    }

    if (!privacyAccepted) {
      setErrorMessage('Devi accettare la Privacy Policy per completare la registrazione.');
      setLoading(false);
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/auth/register-client`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          first_name: registerData.first_name,
          last_name: registerData.last_name,
          email: registerData.email,
          phone: registerData.phone,
          password: registerData.password,
          terms_accepted: true,
          privacy_accepted: true
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setErrorMessage(data.detail || 'Errore durante la registrazione');
        setLoading(false);
        return;
      }

      // Success - show message and switch to login
      setSuccessMessage('Registrazione completata! Ora puoi accedere.');
      setRegisterData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        password: '',
        confirm_password: ''
      });
      setTermsAccepted(false);
      setPrivacyAccepted(false);
      
      // Auto switch to login tab after 2 seconds
      setTimeout(() => {
        setActiveTab('login');
        setLoginData({ ...loginData, email: registerData.email });
        setSuccessMessage('');
      }, 2000);
      
    } catch (error) {
      console.error('Errore:', error);
      setErrorMessage('Errore di connessione. Riprova più tardi.');
    }
    setLoading(false);
  };

  const handleClose = () => {
    onOpenChange(false);
    resetForms();
    setActiveTab('login');
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Header with Tabs */}
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {activeTab === 'login' ? 'Accedi' : 'Iscriviti'}
              </h2>
              
              {/* Tab Buttons */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setActiveTab('login'); setErrorMessage(''); setSuccessMessage(''); }}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                    activeTab === 'login'
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Accedi
                </button>
                <button
                  type="button"
                  onClick={() => { setActiveTab('register'); setErrorMessage(''); setSuccessMessage(''); }}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                    activeTab === 'register'
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Iscriviti
                </button>
              </div>
            </div>

            {/* Success Message */}
            {successMessage && (
              <div className="mx-6 mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                {successMessage}
              </div>
            )}

            {/* Login Form */}
            {activeTab === 'login' && (
              <form onSubmit={handleLoginSubmit} className="p-6 space-y-4 relative">
                <div className="absolute top-6 right-6 z-10">
                  <HowItWorksHover variant="client" />
                </div>
                <p className="text-slate-600 text-sm pr-32">
                  Accedi al tuo account cliente.
                </p>
                
                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="login_email">Email *</Label>
                  <Input
                    id="login_email"
                    type="email"
                    value={loginData.email}
                    onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                    placeholder="mario.rossi@example.com"
                    required
                  />
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <Label htmlFor="login_password">Password *</Label>
                  <div className="relative">
                    <Input
                      id="login_password"
                      type={showPassword ? "text" : "password"}
                      value={loginData.password}
                      onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                      placeholder="••••••••"
                      className="pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errorMessage && (
                    <p className="text-red-500 text-sm mt-1">{errorMessage}</p>
                  )}
                </div>

                {/* Password dimenticata */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => {
                      onOpenChange(false);
                      setResetMessage('');
                      setForgotPasswordOpen(true);
                    }}
                    className="text-orange-600 hover:text-orange-700 hover:underline text-sm"
                    data-testid="client-login-forgot-password"
                  >
                    Password dimenticata?
                  </button>
                </div>

                {/* Buttons */}
                <div className="flex space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
                    Annulla
                  </Button>
                  <Button 
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700"
                  >
                    {loading ? 'Accesso...' : 'Accedi'}
                  </Button>
                </div>
              </form>
            )}

            {/* Register Form */}
            {activeTab === 'register' && (
              <form onSubmit={handleRegisterSubmit} className="p-6 space-y-4 relative">
                <div className="absolute top-6 right-6 z-10">
                  <HowItWorksHover variant="client" />
                </div>
                <p className="text-slate-600 text-sm pr-32">
                  Crea un nuovo account cliente per poter contattare il noleggiante.
                </p>
                
                {/* Nome e Cognome */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="reg_first_name">Nome *</Label>
                    <Input
                      id="reg_first_name"
                      type="text"
                      value={registerData.first_name}
                      onChange={(e) => setRegisterData({...registerData, first_name: e.target.value})}
                      placeholder="Mario"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg_last_name">Cognome *</Label>
                    <Input
                      id="reg_last_name"
                      type="text"
                      value={registerData.last_name}
                      onChange={(e) => setRegisterData({...registerData, last_name: e.target.value})}
                      placeholder="Rossi"
                      required
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="reg_email">Email *</Label>
                  <Input
                    id="reg_email"
                    type="email"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                    placeholder="mario.rossi@example.com"
                    required
                  />
                </div>

                {/* Telefono */}
                <div className="space-y-2">
                  <Label htmlFor="reg_phone">Telefono *</Label>
                  <Input
                    id="reg_phone"
                    type="tel"
                    value={registerData.phone}
                    onChange={(e) => setRegisterData({...registerData, phone: e.target.value})}
                    placeholder="+39 333 1234567"
                    required
                  />
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <Label htmlFor="reg_password">Password *</Label>
                  <div className="relative">
                    <Input
                      id="reg_password"
                      type={showPassword ? "text" : "password"}
                      value={registerData.password}
                      onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                      placeholder="Minimo 8 caratteri"
                      className="pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">Maiuscola, minuscola, numero e carattere speciale</p>
                </div>

                {/* Conferma Password */}
                <div className="space-y-2">
                  <Label htmlFor="reg_confirm_password">Conferma Password *</Label>
                  <Input
                    id="reg_confirm_password"
                    type={showPassword ? "text" : "password"}
                    value={registerData.confirm_password}
                    onChange={(e) => setRegisterData({...registerData, confirm_password: e.target.value})}
                    placeholder="Ripeti la password"
                    required
                  />
                  {errorMessage && (
                    <p className="text-red-500 text-sm mt-1">{errorMessage}</p>
                  )}
                </div>

                {/* Termini e Condizioni — obbligatorio */}
                <div className="pt-1">
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                      data-testid="client-terms-checkbox"
                      required
                    />
                    <span className="text-sm text-slate-700 leading-snug">
                      Dichiaro di aver letto e accettato i{' '}
                      <button
                        type="button"
                        onClick={() => setShowTerms(true)}
                        className="text-orange-600 underline hover:text-orange-700 font-medium"
                        data-testid="client-terms-link"
                      >
                        Termini e Condizioni
                      </button>
                      .
                    </span>
                  </label>
                </div>

                {/* Privacy Policy — obbligatorio */}
                <div className="pt-1">
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={privacyAccepted}
                      onChange={(e) => setPrivacyAccepted(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                      data-testid="client-privacy-checkbox"
                      required
                    />
                    <span className="text-sm text-slate-700 leading-snug">
                      Dichiaro di aver letto e accettato la{' '}
                      <button
                        type="button"
                        onClick={() => setShowPrivacy(true)}
                        className="text-orange-600 underline hover:text-orange-700 font-medium"
                        data-testid="client-privacy-link"
                      >
                        Privacy Policy
                      </button>
                      .
                    </span>
                  </label>
                </div>

                {/* Anti-scam warning */}
                <AntiScamWarning className="mt-2" testId="login-register-client-anti-scam-warning" />

                {/* Buttons */}
                <div className="flex space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
                    Annulla
                  </Button>
                  <Button 
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700"
                  >
                    {loading ? 'Registrazione...' : 'Iscriviti'}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
      <TermsDialog isOpen={showTerms} onOpenChange={setShowTerms} termsType="user" />
      <TermsDialog isOpen={showPrivacy} onOpenChange={setShowPrivacy} termsType="privacy" />

      {/* Forgot Password Dialog */}
      <Dialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Recupera Password</DialogTitle>
            <DialogDescription>
              Inserisci la tua email e ti invieremo le istruzioni per reimpostare la password.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleForgotPassword} className="space-y-4">
            {resetMessage && (
              <div
                className={`px-4 py-3 rounded-lg text-sm ${
                  resetMessage.startsWith('✅')
                    ? 'bg-green-50 border border-green-200 text-green-700'
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`}
                data-testid="client-reset-password-msg"
              >
                {resetMessage}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="resetEmailClient">Email</Label>
              <Input
                id="resetEmailClient"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="tua-email@esempio.com"
                required
                data-testid="client-reset-password-email"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setForgotPasswordOpen(false);
                  setResetMessage('');
                }}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700"
                data-testid="client-reset-password-submit"
              >
                Invia Email
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LoginClientDialog;
